/**
 * overlayReservation.ts —— Round26 全局浮层统一避让契约中枢（单机 GameScreen 唯一真源）
 *
 * 破根因（三方联合结论）：此前 5 个浮层（sizer / scoretip / stylecard / toast / settle）各自
 * `absolute`/`fixed` 定位、各自持一段 z-index，**互不知道对方与「底部操作区」占了屏幕哪块**，
 * 于是「修好 A 冒出 B」。本模块把「各自为政」升级为「**受保护区注册 + 浮层占用登记 + 统一让位求解**」：
 *
 *   - 受保护区（PZ1~PZ4）：任何浮层都不得与之 bbox 相交的矩形集合，实测采集（getBoundingClientRect）。
 *       PZ1 底部 ActionBar 全部按钮 + sizer CTA/取消（`.actionbar__main [data-act]` / `.sizer__cta` / `.sizer__cancel`）
 *       PZ2 ActionBar 信息条（`.actionbar__info`，含「你的筹码」）
 *       PZ3 Hero 手牌区（`.felt__seat--me` 及底牌）
 *       PZ4 顶部筹码/局数条（`.topbar`）
 *       ▸ 派生：bottomSafeBandTop = `.actionbar` 顶沿（含 info 条）—— 所有浮层底边的硬下界。
 *   - 让位求解：纯几何函数（可毫秒级单测），约束优先级 ① 不侵占受保护区(硬) ② 浮层间不叠压(硬)
 *       ③ 浮层可矮可滚 ④ 视觉间距最后让。**冲突一律几何让位（上移/收窄/侧移），禁止抬高 z 去盖受保护区。**
 *
 * 层级只排「同为合法落位的浮层之间」的前后关系，取值全部来自 `tokens.css` 的单一有序 `--z-overlay-*` 表
 * （见本文件 `OVERLAY_Z_ORDER`）；受保护区不靠层级保护，其不被盖完全靠这里的几何硬下界。
 *
 * 联机红线：本模块**仅服务单机 GameScreen 路径**，不被 `src/ui/mp/*` 引用；`--z-mp-*` 不受影响。
 */

/** 视口坐标矩形（与 `getBoundingClientRect()` 同口径）。 */
export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** 浮层通用视觉间距（= `--space-2`）。所有浮层底边与受保护区之间的硬间距。 */
export const OVERLAY_GAP = 8;

/**
 * 单机浮层层级的**单一有序真源**（语义 token 名，按占用带优先级从低到高）。
 * 数值定义在 `tokens.css` 一处（`--z-overlay-*` 段），此处只锁定「顺序」用于单测，
 * 不含任何裸值。层级仅表达浮层间前后关系，不用于盖过受保护区（那靠几何让位）。
 */
export const OVERLAY_Z_ORDER = [
  '--z-overlay-root',
  '--z-overlay-scrim',
  '--z-overlay-card',
  '--z-overlay-sizer',
  '--z-overlay-toast',
  '--z-overlay-scoretip',
  '--z-overlay-scoretip-detail',
  '--z-overlay-settle',
  '--z-overlay-alert',
] as const;

export type OverlayZToken = (typeof OVERLAY_Z_ORDER)[number];

/** 两矩形相交面积（无相交 = 0）。null 视为空盒，返回 0。 */
export function intersectArea(a: Rect | null, b: Rect | null): number {
  if (!a || !b) return 0;
  const w = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const h = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return w * h;
}

/** 两矩形是否相交（面积 > eps）。默认 eps=1（抗锯齿容差 ≤1px²）。 */
export function intersects(a: Rect | null, b: Rect | null, eps = 1): boolean {
  return intersectArea(a, b) > eps;
}

/** 多矩形并集包络盒（忽略 null）。全空返回 null。 */
export function unionRect(rects: Array<Rect | null>): Rect | null {
  let out: Rect | null = null;
  for (const r of rects) {
    if (!r) continue;
    out = out
      ? {
          left: Math.min(out.left, r.left),
          top: Math.min(out.top, r.top),
          right: Math.max(out.right, r.right),
          bottom: Math.max(out.bottom, r.bottom),
        }
      : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }
  return out;
}

/** DOM 元素 → Rect（缺失/零盒返回 null）。 */
function rectOf(el: Element | null | undefined): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 && r.height <= 0) return null;
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

/** 采集口径的选择器（设计 §2.2）。集中一处便于维护与红线扫描。 */
export const PROTECTED_SELECTORS = {
  /** PZ1：ActionBar 全部按钮 + sizer CTA/取消 + 弃牌推进二选一。 */
  buttons: ['.actionbar__main [data-act]', '.sizer__cta', '.sizer__cancel', '[data-pf]'],
  /** PZ2：ActionBar 信息条（含「你的筹码」）。 */
  info: '.actionbar__info',
  /** PZ3：Hero 席位（含底牌包络）。 */
  hero: '.felt__seat--me',
  /** PZ3 的底牌牌面（更紧的 Hero 手牌盒，供 sizer 的 heroCardsTop 硬界使用）。 */
  heroCards: '.felt__seat--me .pcard, .felt__seat--me .seat__card',
  /** PZ4：顶部筹码/局数条。 */
  topbar: '.topbar',
  /** 派生带：ActionBar（含 info 条）—— 顶沿即 bottomSafeBandTop。 */
  actionbar: '.actionbar',
} as const;

/** 实测受保护区集合 + 派生硬界。 */
export interface ProtectedZones {
  /** PZ1：按钮并集盒。 */
  pz1Buttons: Rect | null;
  /** PZ2：信息条盒。 */
  pz2Info: Rect | null;
  /** PZ3：Hero 席位盒。 */
  pz3Hero: Rect | null;
  /** PZ3 派生：Hero 底牌上沿（无牌时 = Hero 席位上沿；再无则 null）。 */
  heroCardsTop: number | null;
  /** PZ4：顶栏盒。 */
  pz4Topbar: Rect | null;
  /** 派生带：`.actionbar` 顶沿 = 所有浮层底边硬下界（无则 null）。 */
  bottomSafeBandTop: number | null;
  /** 派生：顶栏下沿（float/详情卡 top 的上安全线）。 */
  topbarBottom: number | null;
}

/**
 * 采集 PZ1~PZ4 的真机 bbox。root 默认 `document`，便于单测传入自定义容器。
 * 与浮层内容无关的硬界（ActionBar 底部锚定 / Hero 席位 / topbar）保证不产生测量回环（设计 §2.2）。
 */
export function collectProtectedZones(root: Document | HTMLElement = document): ProtectedZones {
  const q = <T extends Element = Element>(sel: string): T | null => root.querySelector<T>(sel);
  const qa = (sel: string): Element[] => Array.from(root.querySelectorAll(sel));

  const buttonEls = PROTECTED_SELECTORS.buttons.flatMap((s) => qa(s));
  const pz1Buttons = unionRect(buttonEls.map(rectOf));
  const pz2Info = rectOf(q(PROTECTED_SELECTORS.info));
  const pz3Hero = rectOf(q(PROTECTED_SELECTORS.hero));
  const pz4Topbar = rectOf(q(PROTECTED_SELECTORS.topbar));
  const actionbar = rectOf(q(PROTECTED_SELECTORS.actionbar));

  const heroCardRects = qa(PROTECTED_SELECTORS.heroCards).map(rectOf);
  const heroCardsUnion = unionRect(heroCardRects);
  const heroCardsTop = heroCardsUnion ? heroCardsUnion.top : pz3Hero ? pz3Hero.top : null;

  return {
    pz1Buttons,
    pz2Info,
    pz3Hero,
    heroCardsTop,
    pz4Topbar,
    bottomSafeBandTop: actionbar ? actionbar.top : pz1Buttons ? pz1Buttons.top : null,
    topbarBottom: pz4Topbar ? pz4Topbar.bottom : null,
  };
}

/**
 * 浮层底边的硬下界求解（所有浮层通用）：
 *   bottomY ≤ min(bottomSafeBandTop, heroCardsTop?) − gap
 * 保证浮层永不越过「底部操作受保护带」，且（传入 heroCardsTop 时）不遮 Hero 手牌。
 * 上界不足时不改本函数职责（由调用方决定内部滚动/兜底）。
 */
export function solveOverlayBottomY(input: {
  desiredBottomY: number;
  bottomSafeBandTop: number | null;
  heroCardsTop?: number | null;
  gap?: number;
}): number {
  const gap = input.gap ?? OVERLAY_GAP;
  let bottom = input.desiredBottomY;
  if (input.bottomSafeBandTop != null) bottom = Math.min(bottom, input.bottomSafeBandTop - gap);
  if (input.heroCardsTop != null) bottom = Math.min(bottom, input.heroCardsTop - gap);
  return bottom;
}

/**
 * ScoreTip float 顶边求解（问题2 · 设计 §4.3 C4）：
 *   top = max(base ?? 52, topbarBottom + gap)  —— 不压 PZ4 顶栏。
 */
export function solveFloatTop(input: { topbarBottom: number | null; base?: number; gap?: number }): number {
  const gap = input.gap ?? OVERLAY_GAP;
  const base = input.base ?? 52;
  if (input.topbarBottom == null) return base;
  return Math.max(base, input.topbarBottom + gap);
}

/**
 * ScoreTip float 展开详解的最大高度（问题2 · 设计 §4.3）：
 *   float 详解向下弹（top = 容器下沿 + 6），其底边不得越过 bottomSafeBandTop − gap，
 *   超高则内部滚动。返回可用高度（≥0），供 `.scoretip--float .scoretip__detail{max-height}` 消费。
 */
export function solveFloatDetailMaxH(input: {
  detailTop: number;
  bottomSafeBandTop: number | null;
  gap?: number;
  min?: number;
}): number {
  const gap = input.gap ?? OVERLAY_GAP;
  const min = input.min ?? 0;
  if (input.bottomSafeBandTop == null) return Math.max(min, 0);
  return Math.max(min, Math.floor(input.bottomSafeBandTop - gap - input.detailTop));
}

/**
 * toast 顶边求解（问题4 · 设计 §4.5 C6）：
 *   结算面板在场且与 toast 几何相交时，toast 顶边上移到 `settle.top − toastH − gap`，
 *   落在结算之上的顶栏安全区（不越顶栏下沿）；否则维持默认 top。
 *   注：结算期普通 toast 首选由 `setToastHold` 静默排队（不渲染），本函数是「确需共存」时的几何兜底。
 */
export function solveToastTop(input: {
  defaultTop: number;
  settle: Rect | null;
  toastH: number;
  gap?: number;
  topbarBottom?: number | null;
}): number {
  const gap = input.gap ?? OVERLAY_GAP;
  if (!input.settle) return input.defaultTop;
  const defaultBottom = input.defaultTop + input.toastH;
  // 默认位与结算不相交 → 不动。
  if (defaultBottom + gap <= input.settle.top || input.defaultTop >= input.settle.bottom) return input.defaultTop;
  // 相交 → 上移到结算之上（顶栏安全区），并夹到顶栏下沿之下。
  const lifted = input.settle.top - input.toastH - gap;
  const floor = input.topbarBottom != null ? input.topbarBottom + gap : 0;
  return Math.max(floor, lifted);
}

/**
 * 玩家详情卡的底部安全线（问题3 · 设计 §4.4 C5）：
 *   bottomSafe = min(bottomSafeBandTop, heroTop) − gap（替换旧默认「视口高 − margin」），
 *   **同时把底部操作受保护带（PZ1）与 Hero 席位/手牌区（PZ3）纳入禁入区**：取两者顶沿更高者
 *   （屏幕 y 更小者）作为详情卡底边硬界，于是左下/靠底头像触发时卡强制向上/右上翻转，
 *   卡 bbox 与 PZ1、PZ3 相交面积均为 0（BUG-R26-01：旧实现只收紧到 PZ1，漏了 PZ3 → 压 Hero 底牌）。
 */
export function solveStyleCardBottomSafe(input: {
  bottomSafeBandTop: number | null;
  /** PZ3：Hero 席位/手牌区顶沿（`.felt__seat--me` top，含底牌包络）。 */
  heroTop?: number | null;
  viewportH: number;
  gap?: number;
}): number {
  const gap = input.gap ?? OVERLAY_GAP;
  const tops: number[] = [];
  if (input.bottomSafeBandTop != null) tops.push(input.bottomSafeBandTop);
  if (input.heroTop != null) tops.push(input.heroTop);
  if (tops.length === 0) return input.viewportH - gap;
  return Math.min(...tops) - gap;
}
